import {HttpErrorResponse} from '@angular/common/http';
import {Component, HostListener, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ClaimMeta, Permission} from 'src/app/interfaces/permission.interface';
import {AppService} from 'src/app/services/app.service';
import {DialogService} from '../../services/dialog.service';
import {Addc2uComponent} from './components/addc2u/addc2u.component';
import {formatDate} from '@angular/common';
import {CustomerDomainI, NamedCustomerDomainI} from '../../interfaces/customer-domain.interface';

@Component({
  selector: 'app-permissions-page',
  templateUrl: './permissions-page.component.html',
  styleUrls: ['./permissions-page.component.scss']
})
export class PermissionsPageComponent implements OnInit {
  public permissions: Permission[] = [];
  public scopeDesc: { [scope: string]: string } = {};
  public claimMeta: ClaimMeta = {};
  public scopedCustomerDomains: NamedCustomerDomainI[] = null;
  public claimDesc: { [claim: string]: string } = {};
  public claimUrl: { [claim: string]: string } = {};
  public permissionsInited = false;
  private hash: string;

  public activeMenus: HTMLElement[] = [];

  @HostListener('document:click', ['$event'])
  clickOut() {
    if (this.activeMenus.length) {
      this.activeMenus.forEach(m => {
        m.classList.remove('show');
      });
      this.activeMenus = [];
    }
  }

  constructor(private app: AppService,
              private route: ActivatedRoute,
              private dialog: DialogService,
              @Inject(LOCALE_ID) private locale: string) {
    this.hash = this.route.snapshot.paramMap.get('hash');
    if (this.hash === null) {
      this.hash = undefined;
    }
  }

  public ngOnInit(): void {
    this.app.isAuthed((auth: boolean) => {
      if (auth) {
        if (!this.permissionsInited) {
          this.permissionsInited = true;
          this.loadPermissions().then(() => {
            this.loadCustomerDomains().then(() => {
              if (this.hash && this.canRedeem()) {
                this.app.usedHash = this.hash;
                this.confirmInvitation('init');
              }
            });
          });
        }
      } else {
        let extras;
        if (this.hash) {
          this.app.invitationMd5 = this.hash;
          extras = {
            params: {
              redirectTo: 'einladungs-code/' + this.hash
            }
          };
        }
        this.app.router.navigateByUrl('', extras);
      }
    });
  }

  canRedeem() {
    let hasThisPermission = false;
    if (this.permissions && this.permissions.length) {
      this.permissions.forEach(p => {
        if (p.external_customer_id.toLowerCase() === this.hash) {
          hasThisPermission = true;
          this.claimHighlight(p, true, true);
        }
      });
    }
    return (this.app.usedHash !== this.hash) && !hasThisPermission;
  }

  protected async removeFromPermissions(permission: Permission): Promise<void> {
    if (this.permissions) {
      this.permissions.forEach((p, i) => {
        if (p.id === permission.id) {
          this.permissions.splice(i, 1);
        }
      })
    }
    return;
  }

  public async loadCustomerDomains() {
    const customers: { [name: string]: NamedCustomerDomainI } = {};
    const customerDomains = await this.app.getCustomerDomains();
    for (const domain of customerDomains) {
      let available = false;

      if (this.permissions) {
        const permission = this.permissions.find(perm => perm.claim_name === domain.claimName);
        if (!permission) {
          available = true;
        }
      } else {
        available = true;
      }

      if (available && typeof domain.customer !== 'undefined') {
        if (customers[domain.customer.name]) {
          customers[domain.customer.name].domains.push(domain);
        } else {
          customers[domain.customer.name] = {
            name: domain.customer.name,
            domains: [domain]
          }
        }
      }
    }
    if (this.scopedCustomerDomains && this.scopedCustomerDomains.length) {
      this.pushSingleCustomerAccess(Object.values(customers))
    } else {
      this.scopedCustomerDomains = Object.values(customers);
    }
  }

  public pushSingleCustomerAccess(customers: NamedCustomerDomainI[]) {
    customers.forEach(customer => {
      this.scopedCustomerDomains.find(c => {
        if (c.name === customer.name) {
          customer.domains.forEach(domain => {
            const hasCustomerDomain = c.domains.find(d => d.claimName === domain.claimName);
            if (!hasCustomerDomain) {
              c.domains.push(domain);
            }
          })
        }
      });
    })
  }

  public async loadPermissions(): Promise<void> {
    for await (const scope of await this.app.getScopes()) {
      this.scopeDesc[scope.name] = scope.desc;
    }
    for await (const claim of await this.app.getClaims()) {
      this.claimDesc[claim.name] = claim.desc;
      this.claimUrl[claim.name] = claim.client_uri + this.app.getToken();

      this.claimMeta[claim.name] = {
        highlight: false
      };
    }

    this.permissions = await this.app.getPermissions();
    return;
  }

  getClaimImage(name: string) {
    let imgName = '';
    switch (name) {
      case 'sta_epaper_sta':
        imgName = 'assets/claimCovers/epaper.png';
        break;
      case 'sta_epaper_stabw':
        imgName = 'assets/claimCovers/epaper.png';
        break;
      case 'sta_download_bvz':
        imgName = 'assets/claimCovers/bvz.png';
        break;
      case 'sta_elearning':
        imgName = 'assets/claimCovers/e-learning.png';
        break;
      case 'sta_evergabe_login':
        imgName = 'assets/claimCovers/e-vergabe.svg';
        break;
      case 'sta_www':
        imgName = 'assets/claimCovers/sta-web.png';
        break;
      default:
        imgName = 'assets/claimCovers/epaper.png';
        break;
    }
    return imgName;
  }

  openPermission(p: Permission) {
    window.open(this.claimUrl[p.claim_name], "_blank");
  }

  showInfo(p: Permission) {
    const alert = this.dialog.alert({
      title: 'Informationen über den Zugang',
      subTitle: '',
      text: `
      <h4 matLine>${this.claimDesc[p.claim_name]}</h4>
      <table class="mat-table data-table">
        <tr>
          <th>Zugangs-Nr.</th>
          <td>${p.external_claim_id}</td>
        </tr>
        <tr>
          <th>Kundenummer</th>
          <td>${p.external_customer_id}</td>
        </tr>
        <tr>
          <th>Gültig von</th>
          <td>${this.parseDate(p.start)}</td>
        </tr>
        <tr>
          <th>Gültig bis</th>
          <td>${this.parseDate(p.expires)}</td>
        </tr>
      </table>`,
      buttons: [
        {
          name: 'Schließen',
          color: 'accent',
          type: 'outline',
          classList: 'full-width',
          handler: () => {
            alert.close();
          }
        }
      ]
    })
  }

  showDate(date: string) {
    let d = formatDate(date, 'shortDate', this.locale);
    return (d === '01.01.99' || d === '31.12.99') ? '' : `Gültig bis ${d}`;
  }

  toggleMenu(menu: HTMLElement) {
    if (menu.classList.contains('show')) {
      menu.classList.remove('show');
    } else {
      menu.classList.add('show');
      setTimeout(() => {
        this.activeMenus.push(menu);
      }, 300);
    }
  }

  openNewClaimDialog() {
    const dialRef = this.dialog.open(Addc2uComponent);
    dialRef.afterClosed().subscribe((c2us: Permission[]) => {
      if (c2us && c2us.length) {
        if (this?.permissions.length) {
          for (const c of c2us) {
            if (!this.permissions.find(c2u => c2u.id === c.id)) {
              this.permissions.push(c);
            }
          }
        } else {
          this.permissions = c2us;
        }
      }
    })
  }

  public removePermission(permission: Permission): void {
    const alert = this.app.dialog.alert({
      title: 'Zugang entfernen',
      subTitle: this.claimDesc[permission.claim_name],
      text: '<p>Dieser Vorgang kann nicht Rückgängig gemacht werden.<br>' +
        'Nach dem Löschen benötigen Sie erneut Ihren Einladungs-Code.</p>' +
        '<p>Möchten Sie diesen Zugang nun entfernen?</p>',
      buttons: [
        {
          name: 'Abbrechen',
          color: 'accent',
          type: 'text',
          handler: () => {
            alert.close();
          }
        }, {
          name: 'Zugang entfernen',
          color: 'warn',
          type: 'flat',
          handler: () => {
            alert.close();
            this.app.dialog.loader({
              title: 'Entferne Zugang'
            });
            this.app.deletePermission(permission.id).then(() => {

              this.removeFromPermissions(permission).then(() => {
                this.app.dialog.closeLoader();
                this.app.toast.open('Zugang entfernt');
                this.loadCustomerDomains();
              });

            }).catch((e: Error) => {
              this.app.dialog.closeLoader();
              console.log(e);
              this.app.toast.open('Unbekannter Fehler', null, {
                panelClass: 'warn'
              });
            });
          }
        }
      ]
    });
  }

  public claimHighlight(p?: Permission, doHighlight?: boolean, alert?: boolean) {
    if (doHighlight && p) {
      if (this.claimMeta[p.claim_name]) {
        this.claimMeta[p.claim_name].highlight = true;
        setTimeout(() => {
          this.claimMeta[p.claim_name].highlight = false;
        }, 5000);
      }
      if (alert) {
        const msg = 'Zugang bereits hinzugefügt. Keine weitere Aktion erforderlich';
        this.app.toast.open(msg, null, {
          panelClass: 'warn'
        });
      }
    } else {
      if (p) {
        if (this.claimMeta && this.claimMeta[p.claim_name]) {
          return this.claimMeta[p.claim_name].highlight || false;
        }
      } else {
        for (let k in this.claimMeta) {
          if (this.claimMeta.hasOwnProperty(k)) {
            this.claimMeta[k].highlight = false;
          }
        }
      }
    }
  }

  public claimAvailable(p: Permission) {
    let startDate = typeof p.start === 'string' ? new Date(p.start) : p.start;
    let endDate = typeof p.start === 'string' ? new Date(p.expires) : p.expires;
    const today = new Date();
    return (startDate < today) && (endDate > today);
  }

  public parseDate(date: string | Date) {
    if (typeof date === 'string') {
      let d = formatDate(date, 'shortDate', this.locale);
      return (d === '01.01.99' || d === '31.12.99') ? 'unbegrenzt' : new Date(date).toLocaleString();
    } else {
      return date.toLocaleString();
    }
  }

  public resendInvitation(domain: CustomerDomainI): void {
    this.app.dialog.loader({
      title: 'Versand von Einladungs-E-Mail'
    });
    console.log(domain);
    this.app.sendActivationMail({claimName: domain.claimName, c2c_id: ''}).then(res => {
      console.log(res);
      this.app.dialog.closeLoader();
      this.app.toast.open('Die E-Mail wurde erfolgreich gesendet.');
    }).catch((e: HttpErrorResponse) => {
      console.error(e);
      this.app.dialog.closeLoader();
      let msg = 'Unbekannter Fehler';
      this.app.toast.open(msg, null, {
        panelClass: 'warn'
      });
    });
  }

  public submitClaim(domain: CustomerDomainI): void {
    this.app.dialog.loader({
      title: 'Die Anforderung wird beantragt'
    });
    this.app.claim2customer({claimName: domain.claimName, userId: this.app.user.id}).then(res => {
      console.log(res);
      domain.c2c = true;
      this.app.dialog.closeLoader();
      this.app.toast.open('Die Anfrage wurde gesendet, in wenigen Momenten erhalten Sie eine Bestätigungs-E-Mail.');
    }).catch((e: HttpErrorResponse) => {
      this.app.dialog.closeLoader();
      let msg = '';
      switch (e.status) {
        case 409:
          msg = 'Die Einladung wurde bereits eingereicht.';
          break;
        case 404:
          msg = 'Ressource wurde nicht gefunden.';
          break;
        default:
          msg = 'Unbekannter Fehler';
          break;
      }
      this.app.toast.open(msg, null, {
        panelClass: 'warn'
      });
    });
  }

  disableCustomer2domain(p: Permission) {
    if (this.scopedCustomerDomains) {
      this.scopedCustomerDomains.forEach((scd, index: number) => {
        const i = scd.domains.findIndex(d => d.claimName === p.claim_name);
        if (i > -1) {
          scd.domains.splice(i, 1);
        }
        if (scd.domains.length === 0) {
          this.scopedCustomerDomains.splice(index, 1);
        }
      });
    }
  }

  protected async confirmInvitation(debug?: string): Promise<void> {
    console.log('confirmInvitation')
    this.app.dialog.loader({
      title: 'Überprüfe Einladungs-Code'
    });
    this.claimHighlight();

    // console.log('debug: ', debug);
    this.app.confirmInvitation({
      key: null,
      external_id: null,
      hash: this.hash
    }).then((permissions: Permission[]) => {
      let now = new Date();
      for (const p of permissions) {
        let expired = new Date(p.expires).getTime() < now.getTime();
        console.log('exp' , expired)
        if (!this.permissions.find(c2u => c2u.id === p.id) && !expired) {
          if (this.permissions && this.permissions.length) {
            this.permissions.push(p);
          } else {
            this.permissions = [p];
          }
        }

        this.disableCustomer2domain(p);
      }

      this.app.dialog.closeLoader();
      this.app.toast.open(permissions.length + (permissions.length === 1 ? ' Zugang hinzugefügt' : ' Zugänge hinzugefügt'));
    }).catch((e: HttpErrorResponse) => {
      this.app.dialog.closeLoader();
      this.handleRedemptionError(e);
    });
  }

  handleRedemptionError(e: HttpErrorResponse) {
    let msg = e.error && e.error.msg ? e.error.msg : null;

    switch ((e as HttpErrorResponse).status) {
      case 409:
        msg = msg || 'Einladungs-Code wird bereits verwendet.';
        if (e.error && e.error.data) {
          e.error.data.forEach((p: Permission) => {
            this.claimHighlight(p, true);
          })
        }
        break;
      case 404:
        msg = msg || 'Einladungs-Code wurde nicht gefunden.';
        break;
      default:
        msg = msg || 'Unbekannter Fehler';
        break;
    }

    if (msg) {
      this.app.toast.open(msg, null, {
        panelClass: 'warn'
      });
    }
  }
}
